import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild, Button, Field, Label, Input, Select, Textarea, RadioGroup, Radio } from '@headlessui/react';
import { Fragment } from 'react';
import { FaTimes, FaFileAlt, FaMobile, FaDesktop, FaGlobe, FaArrowLeft, FaArrowRight, FaCheck, FaBuilding, FaUser, FaShieldAlt, FaCog, FaEye, FaApple, FaAndroid, FaFileContract, FaCookieBite, FaHandshake, FaExclamationTriangle, FaUndo, FaPlus } from 'react-icons/fa';

const normalizeWebsiteUrl = value => value && !/^https?:\/\//i.test(value) ? `https://${value}` : (value || '');
const retentionOptions = [
  { value: 'not-applicable', label: 'Not applicable, no personal data collected' },
  { value: 'while-account-active', label: 'While the account is active' },
  { value: 'until-account-deletion', label: 'Until account deletion' },
  { value: '30 days', label: '30 days' },
  { value: '90 days', label: '90 days' },
  { value: '1 year', label: '1 year' },
  { value: '2 years', label: '2 years' },
  { value: 'legal-requirement', label: 'As long as required by law' },
  { value: 'custom', label: 'Custom criteria' }
];
const getRetentionLabel = (value) => retentionOptions.find(option => option.value === value)?.label || value || '';

const DocumentForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  onSuccess = null,
  document = null, 
  projects = [], 
  isLoading = false,
  user = null
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Toast notification states
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Toast notification functions
  const showToast = (message, type = 'error') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  const [formData, setFormData] = useState({
    // Step 1: Document Type
    documentType: 'privacy-policy',
    
    // Step 2: Company Information
    companyName: '',
    companyType: 'individual',
    companyAddress: '',
    companyEmail: '',
    companyPhone: '',
    websiteUrl: '',
    contactPerson: '',
    
    // Step 3: App Information
    appName: '',
    serviceDescription: '',
    appType: 'free',
    platforms: [],
    iosAppUrl: '',
    androidAppUrl: '',
    premiumFeatures: [],
    
    // Step 4: Data Collection
    dataTypes: {
      personalInfo: false,
      contactInfo: false,
      technicalInfo: false,
      locationData: false,
      paymentInfo: false,
      behavioralData: false,
      deviceInfo: false,
      cookieData: false,
      pushNotifications: false,
      cameraAccess: false,
      microphoneAccess: false,
      contactsAccess: false
    },
    customDataTypes: [], // Custom data types added by user
    noDataCollected: false,
    
    // Step 5: Data Usage
    dataUsage: {
      serviceProvision: false,
      marketing: false,
      analytics: false,
      customerSupport: false,
      legal: false,
      improvement: false,
      thirdPartySharing: false
    },
    
    // Step 6: Third Party Services
    thirdPartyServices: {
      // Top 10 popular mobile services - all false by default
      googleAnalytics: false,
      firebaseAnalytics: false,
      stripe: false,
      paypal: false,
      googleSignIn: false,
      facebookLogin: false,
      appleSignIn: false,
      firebaseCloudMessaging: false,
      googleMaps: false,
      crashlytics: false,
      // Legacy fields
      facebookPixel: false,
      paymentProcessors: false,
      emailServices: false,
      cloudStorage: false,
      customServices: []
    },
    
    // Step 7: User Rights
    userRights: {
      dataAccess: true,
      dataCorrection: true,
      dataDeletion: true,
      dataPortability: true,
      optOut: true,
      complaint: true
    },
    
    // Step 8: Legal Compliance
    legalCompliance: {
      gdpr: false,
      ccpa: false,
      pipeda: false,
      lgpd: false
    },
    
    // Step 9: Data Retention
    dataRetention: {
      retentionPeriod: '',
      customPeriod: ''
    },
    legalBasis: '',
    childrenPrivacyDetails: '',
    
    // Additional fields
    industry: 'other',
    targetAudience: {
      minAge: 13,
      isChildrenData: false
    },
    customClauses: [],
    loginEmail: user?.email || '',
    
    // Payment fields
    totalCost: 0,
    paymentStatus: 'pending',
    paymentMethod: '',
    paymentCompletedAt: null,
    
    // Security measures
    securityMeasures: {
      encryption: false,
      accessControl: false,
      regularAudits: false,
      staffTraining: false,
      incidentResponse: false
    },
    
    // Cookie policy
    cookiePolicy: {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false
    },
    
    // Selected Third Party Services
    selectedThirdPartyServices: [],
    
    // Extra Data Fields
    extraDataFields: [],
    
    // Project ID
    projectId: '',
    productId: ''
  });

  const [selectedProject, setSelectedProject] = useState('');
  const [projectProducts, setProjectProducts] = useState([]);
  const [projectProductsLoading, setProjectProductsLoading] = useState(false);
  const [projectProductsError, setProjectProductsError] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const projectRequestRef = useRef(0);

  // Debug: Log current step changes
  useEffect(() => {
    console.log('DocumentForm currentStep changed to:', currentStep);
  }, [currentStep]);

  // Debug: Log projects prop
  useEffect(() => {
    console.log('DocumentForm projects prop:', projects);
    console.log('DocumentForm projects length:', projects?.length || 0);
  }, [projects]);

  // Document type değiştiğinde currentStep'i sıfırlama - bu sorun yaratıyor
  // useEffect(() => {
  //   if (!isLoading) {
  //     setCurrentStep(1);
  //   }
  // }, [formData.documentType, isLoading]);

  // Loading state'i sıfırla - modal kapatma işlemi UserDashboard'da yapılıyor
  useEffect(() => {
    // Loading bittiğinde modal'ı kapatmak için UserDashboard'a bildirim gönder
    const steps = getStepsForDocumentType(formData.documentType);
    if (!isLoading && currentStep === steps.length) {
      // Modal kapatma işlemi UserDashboard'da handleCreateDocument'ın finally bloğunda yapılıyor
      // Burada sadece loading state'ini takip ediyoruz
    }
  }, [isLoading, currentStep, formData.documentType]);

  // Modal açıldığında step'i reset et
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      console.log('Modal opened, resetting currentStep to 1');
    }
  }, [isOpen]);


  // Top 10 most popular mobile services
  const popularMobileServices = [
    { id: 'googleAnalytics', name: 'Google Analytics', category: 'Analytics' },
    { id: 'firebaseAnalytics', name: 'Firebase Analytics', category: 'Analytics' },
    { id: 'stripe', name: 'Stripe', category: 'Payment' },
    { id: 'paypal', name: 'PayPal', category: 'Payment' },
    { id: 'googleSignIn', name: 'Google Sign-In', category: 'Authentication' },
    { id: 'facebookLogin', name: 'Facebook Login', category: 'Authentication' },
    { id: 'appleSignIn', name: 'Apple Sign-In', category: 'Authentication' },
    { id: 'firebaseCloudMessaging', name: 'Firebase Cloud Messaging', category: 'Push Notifications' },
    { id: 'googleMaps', name: 'Google Maps', category: 'Maps & Location' },
    { id: 'crashlytics', name: 'Firebase Crashlytics', category: 'Crash Reporting' }
  ];

  // Dinamik steps sistemi - document type'a göre adımları belirle
  const getStepsForDocumentType = (documentType) => {
    const allSteps = [
      { id: 1, title: 'Document Type', icon: FaFileAlt, required: true },
      { id: 2, title: 'Company Info', icon: FaBuilding, required: true },
      { id: 3, title: 'App Details', icon: FaMobile, required: true },
      { id: 4, title: 'Data Collection', icon: FaShieldAlt, required: false },
      { id: 5, title: 'Data Usage', icon: FaCog, required: false },
      { id: 6, title: 'Third Parties', icon: FaUser, required: false },
      { id: 7, title: 'User Rights', icon: FaEye, required: false },
      { id: 8, title: 'Legal Compliance', icon: FaShieldAlt, required: false },
      { id: 9, title: 'Security & Cookies', icon: FaCog, required: false },
      { id: 10, title: 'Data Retention', icon: FaCog, required: false },
      { id: 11, title: 'Target Audience', icon: FaUser, required: false },
      { id: 13, title: 'Project & App', icon: FaMobile, required: true },
      { id: 14, title: 'Privacy Details', icon: FaShieldAlt, required: true },
      { id: 12, title: 'Generate Document', icon: FaCheck, required: true }
    ];

    // Document type'a göre hangi adımların gerekli olduğunu belirle
    const requiredSteps = {
      'privacy-policy': [1, 13, 14, 12],
      'terms-of-service': [1, 2, 3, 12], // Sadece temel adımlar
      'cookie-policy': [1, 2, 3, 6, 9, 12], // Cookie ile ilgili adımlar
      'data-processing-agreement': [1, 2, 3, 4, 5, 6, 7, 8, 12],
      'disclaimer': [1, 2, 3, 12], // Sadece temel adımlar
      'refund-policy': [1, 2, 3, 12], // Sadece temel adımlar
      'acceptable-use-policy': [1, 2, 3, 12] // Sadece temel adımlar
    };

    const stepsForType = requiredSteps[documentType] || requiredSteps['privacy-policy'];
    
    const result = allSteps
      .filter(step => stepsForType.includes(step.id))
      .map((step, index) => ({ ...step, id: index + 1 })); // ID'leri yeniden numaralandır
    
    // console.log('Steps Debug:', { 
    //   documentType, 
    //   requiredSteps: stepsForType, 
    //   result: result.map(s => ({ id: s.id, title: s.title }))
    // });
    
    return result;
  };

  const steps = getStepsForDocumentType(formData.documentType);

  // User bilgileri ile form data'sını doldur
  useEffect(() => {
    if (isOpen && !document && user) {
      // Sadece yeni döküman oluştururken ve user bilgileri varsa doldur
      setFormData(prev => ({
        ...prev,
        companyName: user?.companyName || user?.name || prev.companyName,
        companyEmail: user?.email || prev.companyEmail,
        contactPerson: user?.name || prev.contactPerson
      }));
    }
  }, [isOpen, user, document]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const applyProduct = useCallback((product, project) => {
    if (!product) return;
    const platform = product.platform === 'web' ? 'website' : product.platform;
    setSelectedProductId(product._id);
    setFormData(prev => ({
      ...prev,
      appName: project.name,
      productId: product._id,
      serviceDescription: product.description || project.description || '',
      appType: product.storeInfo?.price?.isFree === false ? 'premium' : prev.appType,
      platforms: platform ? [platform] : [],
      websiteUrl: normalizeWebsiteUrl(product.platform === 'web' ? (product.website || product.storeInfo?.storeUrl || project.website || '') : project.website),
      iosAppUrl: product.platform === 'ios' ? (product.appStoreLinks?.appStore || product.storeInfo?.storeUrl || '') : '',
      androidAppUrl: product.platform === 'android' ? (product.appStoreLinks?.googlePlay || product.storeInfo?.storeUrl || '') : '',
      dataTypes: Object.fromEntries(Object.keys(prev.dataTypes).map(key => [key, false])),
      dataUsage: Object.fromEntries(Object.keys(prev.dataUsage).map(key => [key, false])),
      noDataCollected: false,
      thirdPartyServices: { ...Object.fromEntries(Object.keys(prev.thirdPartyServices).filter(key => key !== 'customServices').map(key => [key, false])), customServices: [] },
      customDataTypes: [],
      selectedThirdPartyServices: [],
      extraDataFields: [],
      legalBasis: '',
      childrenPrivacyDetails: '',
      dataRetention: { retentionPeriod: '', customPeriod: '' },
      targetAudience: { minAge: 13, isChildrenData: false }
    }));
  }, []);

  const handleProjectChange = useCallback(async (projectId) => {
    const requestId = ++projectRequestRef.current;
    const project = projects.find(item => item._id === projectId);
    setSelectedProject(projectId);
    setSelectedProductId('');
    setProjectProducts([]);
    setProjectProductsLoading(!!project);
    setProjectProductsError(false);
    setFormData(prev => ({
      ...prev,
      projectId,
      productId: '',
      companyName: project?.companyName || user?.companyName || '',
      companyType: project?.companyType || 'individual',
      companyAddress: '',
      industry: project?.industry || 'other',
      companyEmail: prev.companyEmail || user?.email || '',
      appName: project?.name || '',
      serviceDescription: project?.description || '',
      appType: 'free',
      websiteUrl: normalizeWebsiteUrl(project?.website),
      platforms: [],
      iosAppUrl: '',
      androidAppUrl: '',
      dataTypes: Object.fromEntries(Object.keys(prev.dataTypes).map(key => [key, false])),
      dataUsage: Object.fromEntries(Object.keys(prev.dataUsage).map(key => [key, false])),
      noDataCollected: false,
      thirdPartyServices: { ...Object.fromEntries(Object.keys(prev.thirdPartyServices).filter(key => key !== 'customServices').map(key => [key, false])), customServices: [] },
      customDataTypes: [],
      selectedThirdPartyServices: [],
      extraDataFields: [],
      legalBasis: '',
      childrenPrivacyDetails: '',
      dataRetention: { retentionPeriod: '', customPeriod: '' },
      targetAudience: { minAge: 13, isChildrenData: false },
      securityMeasures: Object.fromEntries(Object.keys(prev.securityMeasures).map(key => [key, false]))
    }));
    if (!project) return;
    try {
      const { authenticatedFetch } = await import('../utils/tokenUtils');
      const response = await authenticatedFetch(`/api/dashboard/user/projects/${projectId}/products`);
      if (!response.ok) throw new Error('Could not load project products');
      const { products = [] } = await response.json();
      if (requestId !== projectRequestRef.current) return;
      setProjectProducts(products);
      if (products.length === 1) applyProduct(products[0], project);
    } catch (error) {
      if (requestId !== projectRequestRef.current) return;
      console.error('Could not load project products:', error);
      setProjectProductsError(true);
      showToast('Project products could not be loaded. Please try again.');
    } finally {
      if (requestId === projectRequestRef.current) setProjectProductsLoading(false);
    }
  }, [projects, user, applyProduct]);

  const handlePlatformToggle = (platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const handleDataTypeToggle = (dataType) => {
    setFormData(prev => ({
      ...prev,
      noDataCollected: false,
      dataTypes: {
        ...prev.dataTypes,
        [dataType]: !prev.dataTypes[dataType]
      }
    }));
  };

  const handleAddCustomDataType = () => {
    const customType = prompt('Enter custom data type name:');
    if (customType && customType.trim()) {
      setFormData(prev => ({
        ...prev,
        noDataCollected: false,
        customDataTypes: [...(Array.isArray(prev.customDataTypes) ? prev.customDataTypes : []), { name: customType.trim(), enabled: true }]
      }));
    }
  };

  const handleRemoveCustomDataType = (index) => {
    setFormData(prev => ({
      ...prev,
      customDataTypes: (Array.isArray(prev.customDataTypes) ? prev.customDataTypes : []).filter((_, i) => i !== index)
    }));
  };

  const handleToggleCustomDataType = (index) => {
    setFormData(prev => ({
      ...prev,
      customDataTypes: (Array.isArray(prev.customDataTypes) ? prev.customDataTypes : []).map((item, i) => 
        i === index ? { ...item, enabled: !item.enabled } : item
      )
    }));
  };

  const handleDataUsageToggle = (usage) => {
    setFormData(prev => ({
      ...prev,
      noDataCollected: false,
      dataUsage: {
        ...prev.dataUsage,
        [usage]: !prev.dataUsage[usage]
      }
    }));
  };

  const handleNoDataCollectedToggle = (checked) => {
    setFormData(prev => ({
      ...prev,
      noDataCollected: checked,
      dataTypes: checked ? Object.fromEntries(Object.keys(prev.dataTypes).map(key => [key, false])) : prev.dataTypes,
      customDataTypes: checked ? [] : prev.customDataTypes,
      dataUsage: checked ? Object.fromEntries(Object.keys(prev.dataUsage).map(key => [key, false])) : prev.dataUsage,
      legalBasis: checked ? '' : prev.legalBasis,
      thirdPartyServices: checked ? { ...Object.fromEntries(Object.keys(prev.thirdPartyServices).filter(key => key !== 'customServices').map(key => [key, false])), customServices: [] } : prev.thirdPartyServices,
      selectedThirdPartyServices: checked ? [] : prev.selectedThirdPartyServices,
      dataRetention: checked ? { retentionPeriod: 'not-applicable', customPeriod: '' } : (prev.dataRetention.retentionPeriod === 'not-applicable' ? { retentionPeriod: '', customPeriod: '' } : prev.dataRetention)
    }));
  };

  const handleThirdPartyToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      thirdPartyServices: {
        ...prev.thirdPartyServices,
        [service]: !prev.thirdPartyServices[service]
      }
    }));
  };

  const handleThirdPartyServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      thirdPartyServices: {
        ...prev.thirdPartyServices,
        [serviceId]: !prev.thirdPartyServices[serviceId]
      }
    }));
  };


  const removeCustomService = (index) => {
    setFormData(prev => ({
      ...prev,
      thirdPartyServices: {
        ...prev.thirdPartyServices,
        customServices: prev.thirdPartyServices.customServices.filter((_, i) => i !== index)
      }
    }));
  };

  const handleUserRightsToggle = (right) => {
    setFormData(prev => ({
      ...prev,
      userRights: {
        ...prev.userRights,
        [right]: !prev.userRights[right]
      }
    }));
  };

  const handleLegalComplianceToggle = (compliance) => {
    setFormData(prev => ({
      ...prev,
      legalCompliance: {
        ...prev.legalCompliance,
        [compliance]: !prev.legalCompliance[compliance]
      }
    }));
  };

  const validateCurrentStep = () => {
    const errors = [];
    const hasCollectedData = formData.noDataCollected || Object.values(formData.dataTypes).some(Boolean) || formData.customDataTypes.some(item => item.enabled);
    const requiresProcessingDetails = !formData.noDataCollected;
    
    switch (steps[currentStep - 1]?.title) {
      case 'Document Type':
        // Document Type - no validation needed
        break;
        
      case 'Company Info':
        // Company Information
        if (!formData.companyName || formData.companyName.trim().length < 2) {
          errors.push('Company name is required and must be at least 2 characters long');
        }
        if (!formData.companyEmail || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.companyEmail)) {
          errors.push('Valid company email is required');
        }
        break;
        
      case 'App Details':
        // App Information
        if (!formData.appName || formData.appName.trim().length < 2) {
          errors.push('App/Service name is required and must be at least 2 characters long');
        }
        break;

      case 'Data Collection':
        if (formData.documentType === 'privacy-policy' && !hasCollectedData) {
          errors.push('Select the data your service collects');
        }
        break;

      case 'Data Usage':
        if (formData.documentType === 'privacy-policy' && requiresProcessingDetails && !Object.values(formData.dataUsage).some(Boolean)) {
          errors.push('Select at least one actual use of the data');
        }
        break;

      case 'Project & App':
        if (!selectedProject) errors.push('Select the project for this policy');
        if (projectProductsLoading) errors.push('Wait for project products to load');
        if (projectProductsError) errors.push('Project products could not be loaded; select the project again');
        if (projectProducts.length > 1 && !selectedProductId) errors.push('Select the product for this policy');
        if (formData.companyName.trim().length < 2) errors.push('Enter the data controller name');
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.companyEmail)) errors.push('Enter a valid privacy contact email');
        if (formData.appName.trim().length < 2) errors.push('Enter the app or service name');
        break;

      case 'Privacy Details':
        if (!hasCollectedData) errors.push('Select the data your service collects');
        if (requiresProcessingDetails && !Object.values(formData.dataUsage).some(Boolean)) errors.push('Select how the data is used');
        if (requiresProcessingDetails && !formData.legalBasis.trim()) errors.push('Enter the legal basis for the selected purposes');
        if (!formData.dataRetention.retentionPeriod) errors.push('Select the retention period or criteria');
        if (requiresProcessingDetails && formData.dataRetention.retentionPeriod === 'not-applicable') errors.push('Select a retention period for the data you collect');
        if (formData.dataRetention.retentionPeriod === 'custom' && !formData.dataRetention.customPeriod.trim()) errors.push('Enter the custom retention criteria');
        if (formData.targetAudience.isChildrenData && !formData.childrenPrivacyDetails.trim()) errors.push('Explain how children’s data is handled');
        break;
        
      default:
        break;
    }
    
    return errors;
  };

  const nextStep = () => {
    const errors = validateCurrentStep();
    
    if (errors.length > 0) {
      showToast(errors.join(', '), 'error');
      return;
    }
    
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // Sadece son adımda submit yap
    if (currentStep !== steps.length) {
      console.log('Not on last step, ignoring submit');
      return;
    }
    
    // Validate required fields
    const errors = [];
    
    if (!formData.companyName || formData.companyName.trim().length < 2) {
      errors.push('Company name is required and must be at least 2 characters long');
    }
    
    if (!formData.appName || formData.appName.trim().length < 2) {
      errors.push('App/Service name is required and must be at least 2 characters long');
    }
    
    if (!formData.companyEmail || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.companyEmail)) {
      errors.push('Valid company email is required');
    }
    
    if (formData.companyAddress && formData.companyAddress.trim().length < 10) {
      errors.push('Company address must be at least 10 characters long if provided');
    }
    
    if (formData.contactPerson && formData.contactPerson.trim().length < 2) {
      errors.push('Contact person name must be at least 2 characters long if provided');
    }
    
    if (formData.websiteUrl && !/^https?:\/\/.+/.test(formData.websiteUrl)) {
      errors.push('Valid website URL is required if provided (must start with http:// or https://)');
    }
    const hasCollectedData = formData.noDataCollected || Object.values(formData.dataTypes).some(Boolean) || formData.customDataTypes.some(item => item.enabled);
    const requiresProcessingDetails = formData.documentType === 'privacy-policy' && !formData.noDataCollected;
    if (formData.documentType === 'privacy-policy' && !hasCollectedData) {
      errors.push('Select the data your service collects');
    }
    if (requiresProcessingDetails && !Object.values(formData.dataUsage).some(Boolean)) {
      errors.push('Select at least one actual use of the data');
    }
    if (formData.documentType === 'privacy-policy') {
      if (!selectedProject) errors.push('Select the project for this policy');
      if (projectProductsLoading || projectProductsError) errors.push('Project products must be loaded');
      if (projectProducts.length > 1 && !selectedProductId) errors.push('Select the product for this policy');
      if (requiresProcessingDetails && !formData.legalBasis.trim()) errors.push('Enter the legal basis for the selected purposes');
      if (!formData.dataRetention.retentionPeriod) errors.push('Select the retention period or criteria');
      if (requiresProcessingDetails && formData.dataRetention.retentionPeriod === 'not-applicable') errors.push('Select a retention period for the data you collect');
      if (formData.dataRetention.retentionPeriod === 'custom' && !formData.dataRetention.customPeriod.trim()) errors.push('Enter the custom retention criteria');
      if (formData.targetAudience.isChildrenData && !formData.childrenPrivacyDetails.trim()) errors.push('Explain how children’s data is handled');
    }
    
    if (errors.length > 0) {
      showToast(errors.join(', '), 'error');
      return;
    }
    
    // Loading state'i başlat
    setIsGenerating(true);
    
    try {
      console.log('DocumentForm submitting with data:', formData);
      const result = await onSubmit(formData);
      
      // Başarılı olduğunda modal'ı kapat
      if (result) {
        // Modal'ı kapat
        onClose();
        // Verileri yenilemek için parent component'e callback gönder (geciktirilmiş)
        if (typeof onSuccess === 'function') {
          setTimeout(() => {
            onSuccess();
          }, 100); // 100ms gecikme ile modal kapatıldıktan sonra verileri yenile
        }
      }
    } catch (error) {
      console.error('Error generating document:', error);
      showToast('Error generating document', 'error');
    } finally {
      // Loading state'i bitir
      setIsGenerating(false);
    }
  };

  const renderStepContent = () => {
    // Mevcut step'in hangi content'i render etmesi gerektiğini belirle
    const currentStepData = steps[currentStep - 1];
    if (!currentStepData) return null;
    
    // Step title'a göre hangi content'i render edeceğimizi belirle
    const stepContentMap = {
      'Document Type': 1,
      'Company Info': 2,
      'App Details': 3,
      'Data Collection': 4,
      'Data Usage': 5,
      'Third Parties': 6,
      'User Rights': 7,
      'Legal Compliance': 8,
      'Security & Cookies': 9,
      'Data Retention': 10,
      'Target Audience': 11,
      'Generate Document': 12,
      'Project & App': 13,
      'Privacy Details': 14
    };
    
    const contentType = stepContentMap[currentStepData.title];
    
    switch (contentType) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">
                {document ? 'Document Type' : 'Choose Document Type'}
              </h3>
              <p className="text-gray-400">
                {document ? 'Document type cannot be changed when editing' : 'Select the type of legal document you want to generate'}
              </p>
            </div>
            
            <RadioGroup value={formData.documentType} onChange={(value) => handleInputChange('documentType', value)} disabled={!!document}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Radio value="privacy-policy" className={({ checked }) => 
                  `group relative flex items-center p-4 rounded-lg border transition-colors ${
                    document ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${
                    checked 
                      ? 'bg-indigo-600 border-indigo-500 text-white' 
                      : 'bg-gray-800 border-gray-700 hover:border-indigo-500 text-gray-300'
                  }`
                }>
                  <div className="flex items-center space-x-3">
                    <FaShieldAlt className="h-5 w-5 text-blue-400" />
                    <div>
                      <div className="font-medium">Privacy Policy</div>
                      <div className="text-sm opacity-75">A privacy policy based on your selected project and actual data practices</div>
                    </div>
                  </div>
                </Radio>
                
                <Radio value="terms-of-service" className={({ checked }) => 
                  `group relative flex items-center p-4 rounded-lg border transition-colors ${
                    document ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${
                    checked 
                      ? 'bg-indigo-600 border-indigo-500 text-white' 
                      : 'bg-gray-800 border-gray-700 hover:border-indigo-500 text-gray-300'
                  }`
                }>
                  <div className="flex items-center space-x-3">
                    <FaFileContract className="h-5 w-5 text-green-400" />
                    <div>
                      <div className="font-medium">Terms of Service</div>
                      <div className="text-sm opacity-75">Legal terms and conditions for your service usage</div>
                    </div>
                  </div>
                </Radio>
                
                <Radio value="cookie-policy" className={({ checked }) => 
                  `group relative flex items-center p-4 rounded-lg border transition-colors ${
                    document ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${
                    checked 
                      ? 'bg-indigo-600 border-indigo-500 text-white' 
                      : 'bg-gray-800 border-gray-700 hover:border-indigo-500 text-gray-300'
                  }`
                }>
                  <div className="flex items-center space-x-3">
                    <FaCookieBite className="h-5 w-5 text-yellow-400" />
                    <div>
                      <div className="font-medium">Cookie Policy</div>
                      <div className="text-sm opacity-75">Detailed cookie usage and tracking policy</div>
                    </div>
                  </div>
                </Radio>

                <Radio value="data-processing-agreement" className={({ checked }) => 
                  `group relative flex items-center p-4 rounded-lg border transition-colors ${
                    document ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${
                    checked 
                      ? 'bg-indigo-600 border-indigo-500 text-white' 
                      : 'bg-gray-800 border-gray-700 hover:border-indigo-500 text-gray-300'
                  }`
                }>
                  <div className="flex items-center space-x-3">
                    <FaHandshake className="h-5 w-5 text-purple-400" />
                    <div>
                      <div className="font-medium">Data Processing Agreement</div>
                      <div className="text-sm opacity-75">GDPR compliant data processing agreement</div>
                    </div>
                  </div>
                </Radio>

                <Radio value="disclaimer" className={({ checked }) => 
                  `group relative flex items-center p-4 rounded-lg border transition-colors ${
                    document ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${
                    checked 
                      ? 'bg-indigo-600 border-indigo-500 text-white' 
                      : 'bg-gray-800 border-gray-700 hover:border-indigo-500 text-gray-300'
                  }`
                }>
                  <div className="flex items-center space-x-3">
                    <FaExclamationTriangle className="h-5 w-5 text-red-400" />
                    <div>
                      <div className="font-medium">Disclaimer</div>
                      <div className="text-sm opacity-75">Legal disclaimer and liability limitations</div>
                    </div>
                  </div>
                </Radio>

                <Radio value="refund-policy" className={({ checked }) => 
                  `group relative flex items-center p-4 rounded-lg border transition-colors ${
                    document ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${
                    checked 
                      ? 'bg-indigo-600 border-indigo-500 text-white' 
                      : 'bg-gray-800 border-gray-700 hover:border-indigo-500 text-gray-300'
                  }`
                }>
                  <div className="flex items-center space-x-3">
                    <FaUndo className="h-5 w-5 text-orange-400" />
                    <div>
                      <div className="font-medium">Refund Policy</div>
                      <div className="text-sm opacity-75">Clear refund and cancellation terms</div>
                    </div>
                  </div>
                </Radio>
              </div>
            </RadioGroup>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">Company Information</h3>
              <p className="text-gray-400">Tell us about your company or organization</p>
            </div>

            {/* Project Selection */}
            <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  Select Project (optional)
                </Label>
              <Select
                value={selectedProject}
                onChange={(e) => handleProjectChange(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Choose a project...</option>
                {projects.length === 0 ? (
                  <option value="" disabled>No projects available</option>
                ) : (
                  projects.map(project => (
                    <option key={project._id} value={project._id}>
                      {project.name} ({project.companyName})
                    </option>
                  ))
                )}
              </Select>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  Company Name *
                </Label>
                <Input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter company name"
                  required
                />
              </Field>

              {formData.documentType !== 'privacy-policy' && (
              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  Company Type
                </Label>
                <Select
                  value={formData.companyType}
                  onChange={(e) => handleInputChange('companyType', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="individual">Individual</option>
                  <option value="company">Company</option>
                  <option value="LLC">LLC</option>
                  <option value="Corporation">Corporation</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Sole Proprietorship">Sole Proprietorship</option>
                </Select>
              </Field>
              )}

              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  Company Email *
                </Label>
                <Input
                  type="email"
                  value={formData.companyEmail}
                  onChange={(e) => handleInputChange('companyEmail', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="company@example.com"
                  required
                />
              </Field>


              {formData.documentType !== 'privacy-policy' && (
              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  Contact Person
                </Label>
                <Input
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Contact person name"
                />
              </Field>
              )}

              {formData.documentType !== 'privacy-policy' && (
              <Field className="md:col-span-2">
                <Label className="block text-sm font-medium text-white mb-2">
                  Company Address
                </Label>
                <Textarea
                  value={formData.companyAddress}
                  onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter company address"
                />
              </Field>
              )}

              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  Website URL
                </Label>
                <Input
                  type="url"
                  value={formData.websiteUrl}
                  onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="https://example.com"
                />
              </Field>

              {formData.documentType !== 'privacy-policy' && (
              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  value={formData.companyPhone}
                  onChange={(e) => handleInputChange('companyPhone', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="+1 (555) 123-4567"
                />
              </Field>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">App Details</h3>
              <p className="text-gray-400">
                {selectedProject && projects.find(p => p._id === selectedProject)?.products?.length > 0 
                  ? "App details auto-filled from your project's products. You can edit them below:"
                  : "Tell us about your application or service"
                }
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  App/Service Name *
                </Label>
                <Input
                  type="text"
                  value={formData.appName}
                  onChange={(e) => handleInputChange('appName', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter app name"
                  required
                />
              </Field>

              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  App Type
                </Label>
                <Select
                  value={formData.appType}
                  onChange={(e) => handleInputChange('appType', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="free">Free</option>
                  <option value="freemium">Freemium</option>
                  <option value="premium">Premium</option>
                </Select>
              </Field>
            </div>

            <Field>
              <Label className="block text-sm font-medium text-white mb-2">
                Platforms
              </Label>
              <p className="text-xs text-gray-400 mb-3">
                {selectedProject && projects.find(p => p._id === selectedProject)?.products?.length > 0 
                  ? "Platforms auto-filled from your project's products. You can edit them below:"
                  : "Select the platforms your app/service supports:"
                }
              </p>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => handlePlatformToggle('website')}
                  className={`flex items-center justify-center p-3 rounded-lg border transition-colors ${
                    formData.platforms.includes('website')
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <FaGlobe className="h-4 w-4 mr-2" />
                  Website
                </button>
                <button
                  type="button"
                  onClick={() => handlePlatformToggle('ios')}
                  className={`flex items-center justify-center p-3 rounded-lg border transition-colors ${
                    formData.platforms.includes('ios')
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <FaApple className="h-4 w-4 mr-2" />
                  iOS
                </button>
                <button
                  type="button"
                  onClick={() => handlePlatformToggle('android')}
                  className={`flex items-center justify-center p-3 rounded-lg border transition-colors ${
                    formData.platforms.includes('android')
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <FaAndroid className="h-4 w-4 mr-2" />
                  Android
                </button>
              </div>
              
              {/* Show products info if project is selected */}
              {selectedProject && projects.find(p => p._id === selectedProject)?.products?.length > 0 && (
                <div className="mt-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-300 mb-2">Products in this project:</h4>
                  <div className="space-y-1">
                    {projects.find(p => p._id === selectedProject).products.map((product, index) => (
                      <div key={index} className="flex items-center text-xs text-gray-300">
                        <span className="mr-2">
                          {product.platform === 'ios' ? (
                            <FaApple className="w-3 h-3 text-gray-300" />
                          ) : product.platform === 'android' ? (
                            <FaAndroid className="w-3 h-3 text-green-400" />
                          ) : (
                            <FaGlobe className="w-3 h-3 text-blue-400" />
                          )}
                        </span>
                        <span>{product.name || 'Unnamed Product'} ({product.platform})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Field>

            {formData.platforms.includes('ios') && (
              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  iOS App URL
                </Label>
                <Input
                  type="url"
                  value={formData.iosAppUrl}
                  onChange={(e) => handleInputChange('iosAppUrl', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="https://apps.apple.com/app/your-app"
                />
              </Field>
            )}

            {formData.platforms.includes('android') && (
              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  Android App URL
                </Label>
                <Input
                  type="url"
                  value={formData.androidAppUrl}
                  onChange={(e) => handleInputChange('androidAppUrl', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="https://play.google.com/store/apps/details?id=com.yourapp"
                />
              </Field>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">Data Collection</h3>
              <p className="text-gray-400">What types of data does your app collect?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(formData.dataTypes).map(([key, value]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleDataTypeToggle(key)}
                  className={`flex items-center p-4 rounded-lg border transition-colors ${
                    value
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      value ? 'bg-white border-white' : 'border-gray-400'
                    }`}>
                      {value && <FaCheck className="w-2 h-2 text-indigo-600" />}
                    </div>
                    <span className="text-sm font-medium capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Custom Data Types */}
            {formData.customDataTypes && formData.customDataTypes.length > 0 && (
              <div className="mt-6">
                <h4 className="text-md font-medium text-white mb-3">Custom Data Types</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.customDataTypes.map((customType, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                        customType.enabled
                          ? 'bg-indigo-600 border-indigo-500 text-white'
                          : 'bg-gray-800 border-gray-700 text-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() => handleToggleCustomDataType(index)}
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            customType.enabled ? 'bg-white border-white' : 'border-gray-400'
                          }`}
                        >
                          {customType.enabled && <FaCheck className="w-2 h-2 text-indigo-600" />}
                        </button>
                        <span className="text-sm font-medium">{customType.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomDataType(index)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <FaTimes className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Custom Data Type Button */}
            <div className="mt-6">
              <button
                type="button"
                onClick={handleAddCustomDataType}
                className="flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
              >
                <FaPlus className="mr-2 h-4 w-4" />
                Add Custom Data Type
              </button>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">Data Usage</h3>
              <p className="text-gray-400">How do you use the collected data?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(formData.dataUsage).map(([key, value]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleDataUsageToggle(key)}
                  className={`flex items-center p-4 rounded-lg border transition-colors ${
                    value
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      value ? 'bg-white border-white' : 'border-gray-400'
                    }`}>
                      {value && <FaCheck className="w-2 h-2 text-indigo-600" />}
                    </div>
                    <span className="text-sm font-medium capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">Third Party Services</h3>
              <p className="text-gray-400">Select the third-party services you use in your app</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {popularMobileServices.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => handleThirdPartyServiceToggle(service.id)}
                  className={`flex items-center p-3 rounded-lg border transition-colors ${
                    formData.thirdPartyServices[service.id]
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      formData.thirdPartyServices[service.id] ? 'bg-white border-white' : 'border-gray-400'
                    }`}>
                      {formData.thirdPartyServices[service.id] && <FaCheck className="w-2 h-2 text-indigo-600" />}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium">{service.name}</div>
                      <div className="text-xs opacity-75">{service.category}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Custom Services */}
            <div className="mt-6">
              <h4 className="text-md font-medium text-white mb-3">Custom Services</h4>
              
              {/* Add Custom Service Input */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Enter custom service name and press Enter"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const serviceName = e.target.value.trim();
                      if (serviceName) {
                        setFormData(prev => ({
                          ...prev,
                          thirdPartyServices: {
                            ...prev.thirdPartyServices,
                            customServices: [...(prev.thirdPartyServices.customServices || []), { name: serviceName }]
                          }
                        }));
                        e.target.value = '';
                      }
                    }
                  }}
                />
              </div>
              
              {/* Custom Services List */}
              {formData.thirdPartyServices.customServices && formData.thirdPartyServices.customServices.length > 0 && (
                <div className="space-y-2">
                  {formData.thirdPartyServices.customServices.map((service, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                      <span className="text-white text-sm">{service.name}</span>
                      <button
                        type="button"
                        onClick={() => removeCustomService(index)}
                        className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">User Rights</h3>
              <p className="text-gray-400">What rights do users have regarding their data?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(formData.userRights).map(([key, value]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleUserRightsToggle(key)}
                  className={`flex items-center p-4 rounded-lg border transition-colors ${
                    value
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      value ? 'bg-white border-white' : 'border-gray-400'
                    }`}>
                      {value && <FaCheck className="w-2 h-2 text-indigo-600" />}
                    </div>
                    <span className="text-sm font-medium capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">Legal Compliance</h3>
              <p className="text-gray-400">Which privacy laws do you need to comply with?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(formData.legalCompliance).map(([key, value]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleLegalComplianceToggle(key)}
                  className={`flex items-center p-4 rounded-lg border transition-colors ${
                    value
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      value ? 'bg-white border-white' : 'border-gray-400'
                    }`}>
                      {value && <FaCheck className="w-2 h-2 text-indigo-600" />}
                    </div>
                    <span className="text-sm font-medium uppercase">
                      {key}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">Security & Cookies</h3>
              <p className="text-gray-400">Security measures and cookie usage</p>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-md font-medium text-white mb-3">Security Measures</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(formData.securityMeasures).map(([key, value]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleInputChange(`securityMeasures.${key}`, !value)}
                      className={`flex items-center p-4 rounded-lg border transition-colors ${
                        value
                          ? 'bg-indigo-600 border-indigo-500 text-white'
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          value ? 'bg-white border-white' : 'border-gray-400'
                        }`}>
                          {value && <FaCheck className="w-2 h-2 text-indigo-600" />}
                        </div>
                        <span className="text-sm font-medium capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-md font-medium text-white mb-3">Cookie Policy</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(formData.cookiePolicy).map(([key, value]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleInputChange(`cookiePolicy.${key}`, !value)}
                      className={`flex items-center p-4 rounded-lg border transition-colors ${
                        value
                          ? 'bg-indigo-600 border-indigo-500 text-white'
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          value ? 'bg-white border-white' : 'border-gray-400'
                        }`}>
                          {value && <FaCheck className="w-2 h-2 text-indigo-600" />}
                        </div>
                        <span className="text-sm font-medium capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 10:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">Data Retention</h3>
              <p className="text-gray-400">How long do you keep user data?</p>
            </div>

            <Field>
              <Label className="block text-sm font-medium text-white mb-2">
                Retention Period
              </Label>
              <Select
                value={formData.dataRetention.retentionPeriod}
                onChange={(e) => handleInputChange('dataRetention.retentionPeriod', e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="30 days">30 days</option>
                <option value="90 days">90 days</option>
                <option value="1 year">1 year</option>
                <option value="2 years">2 years</option>
                <option value="3 years">3 years</option>
                <option value="5 years">5 years</option>
                <option value="unlimited">Unlimited</option>
                <option value="indefinite">Indefinite</option>
                <option value="custom">Custom period</option>
              </Select>
            </Field>

            {formData.dataRetention.retentionPeriod === 'custom' && (
              <Field>
                <Label className="block text-sm font-medium text-white mb-2">
                  Custom Retention Period
                </Label>
                <Input
                  type="text"
                  value={formData.dataRetention.customPeriod}
                  onChange={(e) => handleInputChange('dataRetention.customPeriod', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="e.g., 18 months, 3 years"
                />
              </Field>
            )}

            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <h4 className="text-md font-medium text-blue-300 mb-2">Ready to Generate!</h4>
              <p className="text-sm text-gray-300 mb-2">
                Your document will be generated based on the information you've provided. 
                You can always edit it later if needed.
              </p>
              <div className="text-xs text-blue-200">
                <strong>Selected retention period:</strong> {formData.dataRetention.retentionPeriod}
                {formData.dataRetention.retentionPeriod === 'custom' && formData.dataRetention.customPeriod && 
                  ` (${formData.dataRetention.customPeriod})`
                }
              </div>
            </div>
          </div>
        );

      case 11:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">Target Audience</h3>
              <p className="text-gray-400">Specify if your service collects data from children</p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isChildrenData"
                  checked={formData.targetAudience.isChildrenData}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    targetAudience: {
                      ...prev.targetAudience,
                      isChildrenData: e.target.checked
                    }
                  }))}
                  className="w-4 h-4 text-indigo-600 bg-gray-800 border-gray-600 rounded focus:ring-indigo-500"
                />
                <label htmlFor="isChildrenData" className="text-white">
                  Our service collects data from children under 13
                </label>
              </div>
              
              {formData.targetAudience.isChildrenData && (
                <div className="ml-7">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Minimum Age
                  </label>
                  <select
                    value={formData.targetAudience.minAge}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      targetAudience: {
                        ...prev.targetAudience,
                        minAge: parseInt(e.target.value)
                      }
                    }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value={13}>13 years</option>
                    <option value={16}>16 years</option>
                    <option value={18}>18 years</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        );

      case 12:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">Generate Document</h3>
              <p className="text-gray-400">Your document is ready to be generated</p>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-6 space-y-4">
              <h4 className="text-white font-medium">Document Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Document Type:</span>
                  <span className="text-white ml-2 capitalize">{formData.documentType.replace('-', ' ')}</span>
                </div>
                <div>
                  <span className="text-gray-400">Company:</span>
                  <span className="text-white ml-2">{formData.companyName}</span>
                </div>
                {formData.documentType === 'privacy-policy' && <div><span className="text-gray-400">Project:</span><span className="text-white ml-2">{projects.find(item => item._id === selectedProject)?.name}</span></div>}
                <div>
                  <span className="text-gray-400">App Name:</span>
                  <span className="text-white ml-2">{formData.appName}</span>
                </div>
                <div>
                  <span className="text-gray-400">Platforms:</span>
                  <span className="text-white ml-2">{formData.platforms.join(', ')}</span>
                </div>
                <div>
                  <span className="text-gray-400">Data Types:</span>
                  <span className="text-white ml-2">
                    {formData.noDataCollected ? 'No personal data collected' : `${Object.entries(formData.dataTypes).filter(([_, value]) => value).length} selected`}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Third Party Services:</span>
                  <span className="text-white ml-2">
                    {Object.entries(formData.thirdPartyServices).filter(([key, value]) => key !== 'customServices' && value === true).length + formData.thirdPartyServices.customServices.length} selected
                  </span>
                </div>
                {formData.documentType === 'privacy-policy' && <div><span className="text-gray-400">Legal basis:</span><span className="text-white ml-2">{formData.noDataCollected ? 'Not applicable' : formData.legalBasis}</span></div>}
                {formData.documentType === 'privacy-policy' && <div><span className="text-gray-400">Retention:</span><span className="text-white ml-2">{formData.dataRetention.retentionPeriod === 'custom' ? formData.dataRetention.customPeriod : getRetentionLabel(formData.dataRetention.retentionPeriod)}</span></div>}
              </div>
            </div>
            
            {/* Demo Template Preview */}
            {formData.documentType !== 'privacy-policy' && (
            <div className="bg-gray-800 rounded-lg p-6 space-y-4">
              <h4 className="text-white font-medium">Document Preview</h4>
              <div className="bg-white rounded-lg p-4 max-h-60 overflow-y-auto">
                <div className="text-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {formData.documentType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </h2>
                  <div className="border-b-2 border-gray-900 mb-4"></div>
                </div>
                
                <div className="text-sm text-gray-700 space-y-2">
                  <p><strong>Company:</strong> {formData.companyName}</p>
                  <p><strong>Service:</strong> {formData.appName}</p>
                  <p><strong>Website:</strong> {formData.websiteUrl || 'Your website URL'}</p>
                  
                  <div className="mt-4">
                    <h3 className="font-semibold text-gray-900 mb-2">1. Information We Collect</h3>
                    <p className="text-gray-600">
                      We collect information you provide directly to us, such as when you create an account, 
                      use our services, or contact us for support...
                    </p>
                  </div>
                  
                  <div className="mt-4">
                    <h3 className="font-semibold text-gray-900 mb-2">2. How We Use Your Information</h3>
                    <p className="text-gray-600">
                      We use the information we collect to provide, maintain, and improve our services, 
                      process transactions, and communicate with you...
                    </p>
                  </div>
                  
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 italic">
                      * This is a preview. Check the generated document against your actual data practices before publishing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}
            
            <div className="text-center">
              <p className="text-gray-400 text-sm">
                Click "Generate Document" to create your complete {formData.documentType.replace('-', ' ')}.
                This process may take a few moments.
              </p>
            </div>
          </div>
        );

      case 13:
        return (
          <div className="space-y-5">
            <p className="text-sm text-gray-400">Choose the project and product this policy covers. Existing details are filled in and can be corrected here.</p>
            <Field>
              <Label className="block text-sm font-medium text-white mb-2">Project *</Label>
              <Select value={selectedProject} onChange={(e) => handleProjectChange(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white">
                <option value="">Choose a project...</option>
                {projects.map(project => <option key={project._id} value={project._id}>{project.name}</option>)}
              </Select>
              {projects.length === 0 && <p className="text-sm text-amber-300 mt-2">Create a project first so this policy can use its details.</p>}
            </Field>
            {projectProducts.length > 1 && (
              <Field>
                <Label className="block text-sm font-medium text-white mb-2">Product *</Label>
                <Select value={selectedProductId} onChange={(e) => {
                  const product = projectProducts.find(item => item._id === e.target.value);
                  const project = projects.find(item => item._id === selectedProject);
                  if (product && project) applyProduct(product, project);
                }} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white">
                  <option value="">Choose the product covered by this policy...</option>
                  {projectProducts.map(product => <option key={product._id} value={product._id}>{product.name || product.platform} ({product.platform})</option>)}
                </Select>
              </Field>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field><Label className="block text-sm font-medium text-white mb-2">Data controller / company *</Label>
                <Input value={formData.companyName} onChange={e => handleInputChange('companyName', e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </Field>
              <Field><Label className="block text-sm font-medium text-white mb-2">Privacy contact email *</Label>
                <Input type="email" value={formData.companyEmail} onChange={e => handleInputChange('companyEmail', e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </Field>
              <Field><Label className="block text-sm font-medium text-white mb-2">Application Name *</Label>
                <Input value={formData.appName} onChange={e => handleInputChange('appName', e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </Field>
              <Field><Label className="block text-sm font-medium text-white mb-2">Business model</Label>
                <Select value={formData.appType} onChange={e => handleInputChange('appType', e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white">
                  <option value="free">Free</option><option value="freemium">Freemium</option><option value="premium">Paid</option>
                </Select>
              </Field>
              <Field className="md:col-span-2"><Label className="block text-sm font-medium text-white mb-2">What the service does</Label>
                <Textarea value={formData.serviceDescription} onChange={e => handleInputChange('serviceDescription', e.target.value)} rows={2} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </Field>
              <Field><Label className="block text-sm font-medium text-white mb-2">Website URL</Label>
                <Input value={formData.websiteUrl} onChange={e => handleInputChange('websiteUrl', e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </Field>
              <Field><Label className="block text-sm font-medium text-white mb-2">Company address (if applicable)</Label>
                <Input value={formData.companyAddress} onChange={e => handleInputChange('companyAddress', e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white" />
              </Field>
            </div>
            <p className="text-sm text-gray-400">Platform: {formData.platforms.join(', ') || 'Project-level service'}.</p>
          </div>
        );

      case 14:
        return (
          <div className="space-y-6">
            <p className="text-sm text-gray-400">Only select practices that actually apply to {formData.appName || 'this service'}.</p>
            {projectProducts.find(item => item._id === selectedProductId)?.technicalInfo?.permissions?.length > 0 && (
              <p className="text-xs text-amber-200">Project permissions to review: {projectProducts.find(item => item._id === selectedProductId).technicalInfo.permissions.join(', ')}</p>
            )}
            <div><h4 className="text-white font-medium mb-2">Data collected *</h4>
              <label className="flex items-center gap-2 text-sm text-gray-200 p-2 rounded bg-gray-800 mb-2">
                <input type="checkbox" checked={formData.noDataCollected} onChange={e => handleNoDataCollectedToggle(e.target.checked)} />
                I do not collect personal data
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(formData.dataTypes).map(([key, value]) => <label key={key} className={`flex items-center gap-2 text-sm p-2 rounded bg-gray-800 ${formData.noDataCollected ? 'text-gray-500' : 'text-gray-200'}`}><input type="checkbox" checked={value} disabled={formData.noDataCollected} onChange={() => handleDataTypeToggle(key)} />{key.replace(/([A-Z])/g, ' $1').trim()}</label>)}
              </div>
              <button type="button" onClick={handleAddCustomDataType} disabled={formData.noDataCollected} className={`text-sm mt-2 ${formData.noDataCollected ? 'text-gray-500' : 'text-indigo-300'}`}>+ Add another data type</button>
              {formData.customDataTypes.filter(item => item.enabled).map((item, index) => <span key={index} className="text-xs text-gray-300 ml-2">{item.name}</span>)}
            </div>
            <div><h4 className="text-white font-medium mb-2">Why it is used *</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(formData.dataUsage).map(([key, value]) => <label key={key} className={`flex items-center gap-2 text-sm p-2 rounded bg-gray-800 ${formData.noDataCollected ? 'text-gray-500' : 'text-gray-200'}`}><input type="checkbox" checked={value} disabled={formData.noDataCollected} onChange={() => handleDataUsageToggle(key)} />{key.replace(/([A-Z])/g, ' $1').trim()}</label>)}
              </div>
            </div>
            <div><h4 className="text-white font-medium mb-2">Third-party services used</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {popularMobileServices.map(service => <label key={service.id} className={`flex items-center gap-2 text-sm p-2 rounded bg-gray-800 ${formData.noDataCollected ? 'text-gray-500' : 'text-gray-200'}`}><input type="checkbox" checked={!!formData.thirdPartyServices[service.id]} disabled={formData.noDataCollected} onChange={() => handleThirdPartyServiceToggle(service.id)} />{service.name}</label>)}
              </div>
              <Input disabled={formData.noDataCollected} placeholder="Other service name (press Enter)" className="w-full mt-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white disabled:text-gray-500" onKeyDown={e => {
                if (e.key !== 'Enter' || !e.target.value.trim()) return;
                e.preventDefault();
                const name = e.target.value.trim();
                setFormData(prev => ({ ...prev, thirdPartyServices: { ...prev.thirdPartyServices, customServices: [...prev.thirdPartyServices.customServices, { name }] } }));
                e.target.value = '';
              }} />
              {formData.thirdPartyServices.customServices.map((service, index) => <span key={index} className="text-xs text-gray-300 mr-2">{service.name} <button type="button" onClick={() => removeCustomService(index)} aria-label={`Remove ${service.name}`}>×</button></span>)}
            </div>
            <Field><Label className="block text-sm font-medium text-white mb-2">Legal basis for the purposes above *</Label>
              <Textarea value={formData.legalBasis} disabled={formData.noDataCollected} onChange={e => handleInputChange('legalBasis', e.target.value)} rows={2} placeholder="For example: service delivery — contract; optional marketing — consent" className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white disabled:text-gray-500" />
            </Field>
            <Field><Label className="block text-sm font-medium text-white mb-2">Retention period or criteria *</Label>
              <Select value={formData.dataRetention.retentionPeriod} onChange={e => {
                handleInputChange('dataRetention.retentionPeriod', e.target.value);
                if (e.target.value !== 'custom') handleInputChange('dataRetention.customPeriod', '');
              }} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white">
                <option value="">Select retention</option>
                {retentionOptions.map(option => <option key={option.value} value={option.value} disabled={option.value === 'not-applicable' && !formData.noDataCollected}>{option.label}</option>)}
              </Select>
            </Field>
            {formData.dataRetention.retentionPeriod === 'custom' && <Field><Label className="block text-sm font-medium text-white mb-2">Custom retention criteria *</Label><Input value={formData.dataRetention.customPeriod} onChange={e => handleInputChange('dataRetention.customPeriod', e.target.value)} placeholder="For example: until account deletion, then 30 days unless law requires longer" className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white" /></Field>}
            <label className="flex items-center gap-2 text-sm text-gray-200"><input type="checkbox" checked={formData.targetAudience.isChildrenData} onChange={e => handleInputChange('targetAudience.isChildrenData', e.target.checked)} />The service processes children's data</label>
            {formData.targetAudience.isChildrenData && <Field><Label className="block text-sm font-medium text-white mb-2">Children's data, consent and safeguards *</Label><Textarea value={formData.childrenPrivacyDetails} onChange={e => handleInputChange('childrenPrivacyDetails', e.target.value)} rows={2} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white" /></Field>}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-[60]">
          <div className={`px-4 py-3 rounded-lg shadow-lg border transition-all duration-300 ${
            toast.type === 'success' 
              ? 'bg-green-900 border-green-700 text-green-300' 
              : 'bg-red-900 border-red-700 text-red-300'
          }`}>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                toast.type === 'success' ? 'bg-green-400' : 'bg-red-400'
              }`}></div>
              <span className="text-sm font-medium">{toast.message}</span>
            </div>
          </div>
        </div>
      )}

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={isGenerating ? () => {} : onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-gray-900 p-6 text-left align-middle shadow-xl transition-all border border-gray-700">
                <DialogTitle as="h3" className="text-lg font-medium leading-6 text-white flex justify-between items-center mb-6">
                  <div className="flex items-center space-x-3">
                    {React.createElement(steps[currentStep - 1].icon, { className: "h-6 w-6 text-indigo-400" })}
                    <span>{document ? 'Edit Document' : `Create ${formData.documentType ? formData.documentType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Document'} Document`}</span>
                  </div>
                  <button 
                    onClick={onClose} 
                    disabled={isGenerating}
                    className={`text-gray-400 hover:text-white transition-colors ${
                      isGenerating ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <FaTimes />
                  </button>
                </DialogTitle>

                {/* Progress Steps */}
                <div className="mb-8">
                  <div className="flex items-center">
                    {steps.map((step, index) => (
                      <div key={step.id} className="flex items-center flex-1">
                        <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                          currentStep >= step.id
                            ? 'bg-indigo-600 border-indigo-600 text-white'
                            : 'border-gray-600 text-gray-400'
                        }`}>
                          {currentStep > step.id ? (
                            <FaCheck className="w-4 h-4" />
                          ) : (
                            <span className="text-sm font-medium">{step.id}</span>
                          )}
                        </div>
                        {index < steps.length - 1 && (
                          <div className="flex-1 mx-2">
                            <div className={`h-0.5 w-full ${
                              currentStep > step.id ? 'bg-indigo-600' : 'bg-gray-600'
                            }`} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-center">
                    <span className="text-sm text-gray-400">
                      Step {currentStep} of {steps.length}: {steps[currentStep - 1].title}
                    </span>
                  </div>
                </div>

                <form>
                  {renderStepContent()}

                  {/* Navigation Buttons */}
                  <div className="flex justify-between items-center pt-6 border-t border-gray-700 mt-8">
                    <Button
                      type="button"
                      onClick={onClose}
                      disabled={isGenerating}
                      className={`px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors ${
                        isGenerating ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      Cancel
                    </Button>

                    <div className="flex space-x-3">
                      <Button
                        type="button"
                        onClick={prevStep}
                        disabled={currentStep === 1 || isGenerating}
                        className="flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FaArrowLeft className="mr-2 h-4 w-4" />
                        Previous
                      </Button>
                      
                      {(() => {
                        // Genel çözüm: Son adımda buton görünür
                        const isLastStep = currentStep === steps.length;
                        // console.log('Button Debug:', { 
                        //   currentStep, 
                        //   stepsLength: steps.length, 
                        //   documentType: formData.documentType, 
                        //   isLastStep,
                        //   result: isLastStep,
                        //   stepsArray: steps.map(s => s.id)
                        // });
                        return isLastStep;
                      })() ? (
                        <Button
                          type="button"
                          onClick={handleSubmit}
                          disabled={isGenerating}
                          className="flex items-center px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isGenerating ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Generating Document...
                            </>
                          ) : (
                            document ? 'Update Document' : 'Generate Document'
                          )}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={nextStep}
                          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          Next
                          <FaArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
    </>
  );
};

export default DocumentForm;
